import { useEffect, useRef, useState } from 'react';
import type { Workflow } from '../types';
import { buildWithAI, SAMPLE_PROMPTS, fetchModels, type AiModel } from '../lib/aiBuilder';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  workflow?: Workflow;
  source?: 'ai' | 'local';
  model?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApplyWorkflow: (wf: Workflow) => void;
}

export function AssistantChat({ open, onClose, onApplyWorkflow }: Props) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      body: "Hi! I'm your AI assistant. Tell me what you want the workflow to do, in plain English, and I'll build the whole thing. Try one of the prompts below to see how it works.",
    },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [models, setModels] = useState<AiModel[]>([]);
  const [model, setModel] = useState<string>('@cf/meta/llama-3.1-8b-instruct-fast');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchModels().then(setModels);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, thinking]);

  async function send(rawText: string) {
    const text = rawText.trim();
    if (!text) return;

    const userMsg: Message = { id: String(Date.now()), role: 'user', body: text };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setThinking(true);

    try {
      const result = await buildWithAI(text, model);
      const wf = result.workflow;
      const triggerNode = wf.nodes.find(n => n.data.kind === 'trigger');
      const actionNode = wf.nodes.find(n => n.data.kind === 'action');
      const conditionCount = wf.nodes.filter(n => n.data.kind === 'condition').length;
      const waitNode = wf.nodes.find(n => n.data.kind === 'wait');
      const aiCount = wf.nodes.filter(n => n.data.kind === 'ai_smart').length;

      const triggerLabel = triggerNode?.data.label || 'unknown trigger';
      const actionLabel = actionNode?.data.label || 'action';
      const waitTxt = waitNode && waitNode.data.kind === 'wait' ? `, then waits ${waitNode.data.amount} ${waitNode.data.unit}` : '';
      const condTxt = conditionCount > 0 ? `, filters by ${conditionCount} condition${conditionCount > 1 ? 's' : ''}` : '';
      const aiTxt = aiCount > 0 ? `, runs ${aiCount} AI step${aiCount > 1 ? 's' : ''}` : '';
      const sourceTag = result.source === 'ai' ? `🦅 ${result.model || 'AI'}` : '🛟 fallback parser';

      const reply: Message = {
        id: String(Date.now() + 1),
        role: 'assistant',
        body: `Built it! ${sourceTag}. The workflow starts **when ${triggerLabel}**${condTxt}${waitTxt}${aiTxt}, then **${actionLabel}**. Tap "Drop on canvas" to load.`,
        workflow: wf,
        source: result.source,
        model: result.model,
      };
      setMessages(m => [...m, reply]);
    } catch (err) {
      setMessages(m => [...m, {
        id: String(Date.now() + 1),
        role: 'assistant',
        body: `Hmm — I hit a snag: ${err instanceof Error ? err.message : 'unknown error'}. Try rephrasing, or pick a different model.`,
      }]);
    } finally {
      setThinking(false);
    }
  }

  if (!open) return null;

  return (
    <div className="assistant-drawer" role="dialog" aria-label="AI workflow builder">
      <div className="assistant-head">
        <div className="assistant-head-left">
          <div className="assistant-icon">★</div>
          <div>
            <b>AI Workflow Assistant</b>
            <span>Describe it · I'll build it</span>
          </div>
        </div>
        <button className="assistant-x" onClick={onClose} aria-label="Close">×</button>
      </div>

      {/* Model picker bar */}
      <div className="assistant-modelbar">
        <span className="assistant-modelbar-label">Model</span>
        <select value={model} onChange={e => setModel(e.target.value)}>
          {(models.length ? models : [{ id: model, label: 'Llama 3.1 8B', desc: '' }]).map(m => (
            <option key={m.id} value={m.id} title={m.desc}>{m.label}</option>
          ))}
        </select>
        <span className="assistant-modelbar-note">
          Free · Cloudflare Workers AI
        </span>
      </div>

      <div className="assistant-messages" ref={listRef}>
        {messages.map(m => (
          <div className={`assistant-msg ${m.role}`} key={m.id}>
            {m.role === 'assistant' && <div className="assistant-msg-av">★</div>}
            <div className="assistant-msg-body">
              <span dangerouslySetInnerHTML={{ __html: m.body.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>') }} />
              {m.workflow && (
                <div className="assistant-workflow-card">
                  <div className="assistant-wf-name">{m.workflow.name}</div>
                  <div className="assistant-wf-stats">
                    {m.workflow.nodes.length} nodes · {m.workflow.edges.length} edges
                    {m.source === 'ai' && m.model && <> · <span style={{ color: 'var(--gold-light)' }}>via {m.model.split('/').pop()}</span></>}
                  </div>
                  <button
                    className="assistant-wf-apply"
                    onClick={() => {
                      onApplyWorkflow(m.workflow!);
                      onClose();
                    }}
                  >
                    Drop on canvas →
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {thinking && (
          <div className="assistant-msg assistant">
            <div className="assistant-msg-av">★</div>
            <div className="assistant-msg-body">
              <div className="assistant-thinking">
                <span className="dot" /><span className="dot" /><span className="dot" />
                Thinking…
              </div>
            </div>
          </div>
        )}
      </div>

      {messages.length === 1 && (
        <div className="assistant-prompts">
          <div className="assistant-prompts-head">Try one of these</div>
          {SAMPLE_PROMPTS.map(p => (
            <button key={p} className="assistant-prompt" onClick={() => send(p)}>
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="assistant-compose">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder="Describe the workflow…"
          rows={2}
        />
        <button className="assistant-send" onClick={() => send(input)} disabled={!input.trim() || thinking}>
          {thinking ? '…' : 'Send →'}
        </button>
      </div>
    </div>
  );
}
